/**
 * Helpers for quote/invoice line rows.
 *
 * A line row has two identities and they are not interchangeable:
 *
 *  - `key` is the row's identity in the browser. Every row has one, it never
 *    changes, and the table, drag-and-drop and the edit/delete actions all use
 *    it. Keys are local only and are stripped before the row is sent.
 *  - `id` is the line's identity in the database. Only a line that has been
 *    saved has one. It is what tells the backend to update that line instead of
 *    deleting it and inserting a replacement, so it must survive an edit.
 */

/**
 * A line id is only meaningful to the backend if it is a saved line's UUID.
 * Anything else (a timestamp used as a placeholder, say) is a local artefact.
 * @param {*} id
 * @returns {boolean}
 */
export const isPersistedLineId = (id) =>
  typeof id === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

/**
 * Next unused row key for a set of lines. Keys are numbers and only have to be
 * unique within the form, so this just walks past the highest one in use.
 * A timestamp is not good enough: two rows added in the same millisecond, or a
 * row added right after an import, can collide.
 * @param {Array<Object>} lines
 * @returns {number}
 */
export const nextLineKey = (lines = []) => {
  const keys = lines
    .map((line) => line?.key)
    .filter((key) => typeof key === "number" && Number.isFinite(key));
  return keys.length > 0 ? Math.max(...keys) + 1 : 1;
};

/**
 * Give every line a key, and a priority matching its position.
 * Used when lines first arrive from the backend.
 * @param {Array<Object>} lines
 * @returns {Array<Object>}
 */
export const withLineKeys = (lines = []) =>
  lines.map((line, index) => ({
    ...line,
    key: index + 1,
    priority: index + 1,
  }));

/**
 * Prepare lines for the API: renumber priorities to the on-screen order and
 * drop anything that only means something in the browser.
 * @param {Array<Object>} lines
 * @returns {Array<Object>}
 */
export const serializeLines = (lines = []) =>
  lines.map((line, index) => {
    const { key, ...rest } = line;
    const serialized = { ...rest, priority: index + 1 };
    if (!isPersistedLineId(serialized.id)) {
      delete serialized.id;
    }
    return serialized;
  });
